import React from 'react';
import classnames from 'classnames';
import Modal from 'react-bootstrap/Modal';
import styles from './InviteTeamMemberModal.module.scss';
import {
  Organization,
  RootState,
  DjangoListResponse,
  MultiDropdownItem,
} from 'v2/models';
import { useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import restFactory from 'v2/utils/restFactory';

import InviteTeamMemberForm from './InviteTeamMemberForm/InviteTeamMemberForm';
import LoadingSpinner from 'v2/common/LoadingSpinner/LoadingSpinner';
import { useBulkCreateUserInvites } from 'v2/queries';
import { useFeatureFlags } from 'v2/hooks/useFeatureFlags';
import { FEATURE_FLAGS } from 'utils/constants';

export type Licenses = {
  label: string;
  value: MultiDropdownItem;
  isDisabled?: boolean;
};

export type BulkInvitation = {
  organization: number;
  inputEmails: {
    emails: string[];
    inputValue: string;
  };
  licenses?: Licenses[];
};

export type UserInviteTeamMemberForm = {
  bulkInvitations: BulkInvitation[];
  invalidEmails: string[];
};

type InviteTeamMemberModalProps = {
  show: boolean;
  onClose: () => void;
};

export const InviteTeamMemberModal = ({
  show,
  onClose,
}: InviteTeamMemberModalProps) => {
  const user = useSelector((state: RootState) => state.user.user);
  const [isInviteColleagueEnabled] = useFeatureFlags([
    FEATURE_FLAGS.INVITE_COLLEAGUE_WITH_LICENSE,
  ]);

  const { data: organizations = [], isLoading: isLoadingOrganizations } =
    useQuery('organizations', async () => {
      const response = await restFactory.get<DjangoListResponse<Organization>>(
        '/dj/api/v1/organizations/'
      );
      return response.results;
    });

  const formContext = useForm<UserInviteTeamMemberForm>({
    defaultValues: {
      bulkInvitations: [
        {
          organization: user.organization.id,
          inputEmails: {
            emails: [],
            inputValue: '',
          },
        },
      ],
      invalidEmails: [],
    },
    mode: isInviteColleagueEnabled ? 'onChange' : 'onSubmit',
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = formContext;

  const { mutate: createUserInvites, isLoading: isCreatingUserInvites } =
    useBulkCreateUserInvites({
      onSuccess: () => {
        reset();
        onClose();
      },
    });

  const sendInvitations = ({ bulkInvitations }: UserInviteTeamMemberForm) => {
    const userInvites = bulkInvitations.flatMap(
      ({ organization, inputEmails: { emails } }) =>
        emails.map((email) => ({
          organization,
          email,
        }))
    );

    return createUserInvites(userInvites);
  };

  return (
    <Modal
      show={show}
      centered
      animation
      onHide={onClose}
      onExited={onClose}
      className={classnames(styles.modal, styles.override)}
    >
      <Modal.Header>
        <Modal.Title>Invite Team Member</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoadingOrganizations ? (
          <LoadingSpinner />
        ) : (
          <FormProvider {...formContext}>
            <InviteTeamMemberForm
              control={control}
              organizations={organizations}
            />
          </FormProvider>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          className="btn btn-light ml-auto"
          type="button"
          onClick={() => {
            reset();
            onClose();
          }}
          disabled={isCreatingUserInvites}
        >
          CANCEL
        </button>

        <form onSubmit={handleSubmit(sendInvitations)}>
          <button
            className="btn btn-primary ml-3 text-nowrap w-auto"
            disabled={!isValid || isCreatingUserInvites}
            type="submit"
          >
            SEND INVITATION
          </button>
        </form>
      </Modal.Footer>
    </Modal>
  );
};
